import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Container,
  CircularProgress,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  Chip,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { authenticatedFetch } from '../../utils/api';
import { useTranslation } from '../../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Employee {
  id: number;
  name: string;
  phone: string;
  address: string;
  salary_type: string;
  monthly_salary: number | null;
  pay_per_shift: number | null;
  created_at: string;
  updated_at: string;
}

const employeeSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  phone: z.string().optional().or(z.literal('')),
  address: z.string().optional().or(z.literal('')),
  salary_type: z.enum(['monthly', 'shift_based']),
  monthly_salary: z.number().optional().nullable(),
  pay_per_shift: z.number().optional().nullable(),
}).refine((data) => {
  if (data.salary_type === 'monthly' && (!data.monthly_salary || data.monthly_salary <= 0)) {
    return false;
  }
  return true;
}, {
  message: "Monthly salary is required for monthly salaried employees",
  path: ["monthly_salary"],
}).refine((data) => {
  if (data.salary_type === 'shift_based' && (!data.pay_per_shift || data.pay_per_shift <= 0)) {
    return false;
  }
  return true;
}, {
  message: "Pay per shift is required for shift-based employees",
  path: ["pay_per_shift"],
});

type EmployeeFormData = z.infer<typeof employeeSchema>;

const EmployeeDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { translations: t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [employee, setEmployee] = useState<Employee | null>(null);

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
  });

  const salaryType = watch('salary_type');

  useEffect(() => {
    fetchEmployee();
  }, [id]);

  const fetchEmployee = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/employees/${id}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch employee');
      }

      const data = await response.json();
      setEmployee(data);
      reset({
        name: data.name,
        phone: data.phone || '',
        address: data.address || '',
        salary_type: data.salary_type || 'shift_based',
        monthly_salary: data.monthly_salary,
        pay_per_shift: data.pay_per_shift,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (data: EmployeeFormData) => {
    try {
      const response = await authenticatedFetch(`${API_URL}/employees/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ employee: data }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData));
      }

      const updatedEmployee = await response.json();
      setEmployee(updatedEmployee);
      setIsEditing(false);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleDelete = async () => {
    if (window.confirm(t.employees.deleteConfirm)) {
      try {
        const response = await authenticatedFetch(`${API_URL}/employees/${id}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          throw new Error('Failed to delete employee');
        }

        navigate('/employees');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm">
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            {isEditing ? t.employees.edit : t.employees.details}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isEditing ? (
            <Box component="form" onSubmit={handleSubmit(handleUpdate)} sx={{ mt: 2 }}>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t.employees.name}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    margin="normal"
                  />
                )}
              />

              <Controller
                name="phone"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={`${t.employees.phone} (${t.optional})`}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    margin="normal"
                  />
                )}
              />

              <Controller
                name="address"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={`${t.companies.address} (${t.optional})`}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    margin="normal"
                    multiline
                    rows={2}
                  />
                )}
              />

              <Controller
                name="salary_type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth margin="normal" error={!!errors.salary_type}>
                    <InputLabel>{t.finance?.salaryType || "Salary Type"}</InputLabel>
                    <Select
                      {...field}
                      label={t.finance?.salaryType || "Salary Type"}
                    >
                      <MenuItem value="shift_based">{t.finance?.shiftBased || "Shift Based"}</MenuItem>
                      <MenuItem value="monthly">{t.finance?.monthly || "Monthly"}</MenuItem>
                    </Select>
                    {errors.salary_type && (
                      <FormHelperText>{errors.salary_type.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              {salaryType === 'monthly' && (
                <Controller
                  name="monthly_salary"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                      label={t.employees?.monthlySalary || "Monthly Salary"}
                      type="number"
                      error={!!errors.monthly_salary}
                      helperText={errors.monthly_salary?.message}
                      fullWidth
                      margin="normal"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  )}
                />
              )}

              {salaryType === 'shift_based' && (
                <Controller
                  name="pay_per_shift"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                      label={t.employees?.payPerShift || "Pay Per Shift"}
                      type="number"
                      error={!!errors.pay_per_shift}
                      helperText={errors.pay_per_shift?.message}
                      fullWidth
                      margin="normal"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        endAdornment: <InputAdornment position="end">/shift</InputAdornment>,
                      }}
                    />
                  )}
                />
              )}

              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  {t.saveChanges}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setIsEditing(false)}
                >
                  {t.cancel}
                </Button>
              </Stack>
            </Box>
          ) : (
            <>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" color="text.secondary">
                  {t.employees.name}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {employee?.name}
                </Typography>

                <Typography variant="subtitle1" color="text.secondary">
                  {t.employees.phone}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {employee?.phone || '-'}
                </Typography>

                <Typography variant="subtitle1" color="text.secondary">
                  {t.companies.address}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {employee?.address || '-'}
                </Typography>

                <Typography variant="subtitle1" color="text.secondary">
                  {t.finance?.salaryType || "Salary Type"}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Chip 
                    label={employee?.salary_type === 'monthly' 
                      ? (t.finance?.monthly || 'Monthly') 
                      : (t.finance?.shiftBased || 'Shift Based')}
                    color={employee?.salary_type === 'monthly' ? 'primary' : 'secondary'}
                    size="small"
                  />
                </Box>

                {employee?.salary_type === 'monthly' && (
                  <>
                    <Typography variant="subtitle1" color="text.secondary">
                      {t.employees?.monthlySalary || "Monthly Salary"}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                      {formatCurrency(employee?.monthly_salary)}
                    </Typography>
                  </>
                )}

                {employee?.salary_type === 'shift_based' && (
                  <>
                    <Typography variant="subtitle1" color="text.secondary">
                      {t.employees?.payPerShift || "Pay Per Shift"}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                      {formatCurrency(employee?.pay_per_shift)} / shift
                    </Typography>
                  </>
                )}

                <Typography variant="subtitle1" color="text.secondary">
                  {t.created}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {new Date(employee?.created_at || '').toLocaleDateString()}
                </Typography>
              </Box>

              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => setIsEditing(true)}
                >
                  {t.edit}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  onClick={handleDelete}
                >
                  {t.delete}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/employees')}
                >
                  {t.back}
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default EmployeeDetails;
